import { history, useIntl, useLocation } from '@umijs/max';
import { Flex, Layout, Menu } from 'antd';
import React, { useMemo } from 'react';
import MyButton from '@/components/MyButton';
import { useTheme } from '@/theme/hooks';

const { Header } = Layout;

export interface NavItem {
  key: string;
  labelKey: string;
  path: string;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { key: 'home', labelKey: 'navigation.home', path: '/' },
  {
    key: 'products',
    labelKey: 'navigation.products',
    path: '/worktool',
    children: [
      {
        key: 'products-worktool',
        labelKey: 'navigation.products.worktool',
        path: '/worktool',
      },
      {
        key: 'products-chatlink',
        labelKey: 'navigation.products.chatlink',
        path: '/qa',
      },
      {
        key: 'products-outbound',
        labelKey: 'navigation.products.outbound',
        path: '/wh',
      },
      {
        key: 'products-huitian-ai',
        labelKey: 'navigation.products.huitianAi',
        path: 'https://tyai.ymdyes.cn/',
      },
      {
        key: 'products-mcp',
        labelKey: 'navigation.products.mcp',
        path: '/mcp',
      },
      {
        key: 'products-ai-gateway',
        labelKey: 'navigation.products.aiGateway',
        path: '/ai-gateway',
      },
    ],
  },
  { key: 'cases', labelKey: 'navigation.cases', path: '/cases' },
  { key: 'private', labelKey: 'navigation.private', path: '/private-deploy' },
  { key: 'about', labelKey: 'navigation.about', path: '/about' },
  { key: 'pricing', labelKey: 'navigation.pricing', path: '/pricing' },
  { key: 'status', labelKey: 'navigation.status', path: '/status-embedded' },
];

const SiteHeader: React.FC = () => {
  const { token } = useTheme();
  const location = useLocation();
  const intl = useIntl();

  const renderBadge = (productKey: string) => {
    const logoMap: Record<string, string> = {
      'products-worktool': '/imgs/logos/worktool-logo.png',
      'products-chatlink': '/imgs/logos/chatlink-logo.png',
      'products-outbound': '/imgs/logos/outbound-call-logo.png',
      'products-huitian-ai': 'https://cdn.asrtts.cn/static/tyai/image/logo.png',
      'products-mcp': '/imgs/logos/mcp-logo.png',
      'products-ai-gateway': '/imgs/logos/ai-gateway-logo.png',
    };

    const logoPath = logoMap[productKey];
    if (logoPath) {
      return (
        <img
          src={logoPath}
          alt={`${productKey} logo`}
          style={{
            width: 24,
            height: 24,
            flexShrink: 0,
          }}
        />
      );
    }

    // 如果没有logo，显示默认的badge
    return (
      <span
        style={{
          width: 24,
          height: 24,
          lineHeight: '24px',
          borderRadius: 999,
          background: token.colorPrimary,
          color: '#fff',
          display: 'inline-block',
          textAlign: 'center',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {productBadgeMap[productKey] || ''}
      </span>
    );
  };

  const productBadgeMap: Record<string, string> = {
    'products-worktool': 'WT',
    'products-chatlink': 'CL',
    'products-outbound': 'WH',
    'products-huitian-ai': 'HT',
    'products-mcp': 'MCP',
    'products-ai-gateway': 'AI',
  };

  const keyToPathMap = useMemo(() => {
    const map = new Map<string, string>();
    navItems.forEach((n) => {
      map.set(n.key, n.path);
      n.children?.forEach((c) => {
        map.set(c.key, c.path);
      });
    });
    return map;
  }, []);

  const activeKey = useMemo(() => {
    // 首先检查是否有子项匹配当前路径
    for (const navItem of navItems) {
      if (navItem.children) {
        const childMatch = navItem.children.find(
          (child) =>
            child.path === location.pathname ||
            (child.path.includes('?') &&
              location.pathname + location.search === child.path),
        );
        if (childMatch) {
          return navItem.key;
        }
      }
    }

    // 然后检查父级路径匹配
    const found = navItems.find((n) =>
      n.path === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(n.path),
    );
    // 如果没有任何菜单匹配当前路径，则不选中任何菜单项
    return found?.key;
  }, [location.pathname, location.search]);

  return (
    <Header
      style={{
        background: token.colorBgContainer,
        padding: '0 24px',
        boxShadow: token.boxShadowSecondary,
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          height: 64,
        }}
      >
        <Flex
          align="center"
          gap={12}
          style={{ cursor: 'pointer' }}
          onClick={() => history.push('/')}
        >
          <img src="/logo.png" alt="logo" style={{ height: 60 }} />
        </Flex>

        <Flex align="center" gap={16}>
          <Menu
            mode="horizontal"
            selectedKeys={activeKey ? [activeKey] : []}
            onClick={(e) => {
              const path = keyToPathMap.get(String(e.key));
              if (!path) return;
              if (path.startsWith('http://') || path.startsWith('https://')) {
                window.open(path, '_blank', 'noopener,noreferrer');
                return;
              }
              history.push(path);
            }}
            items={navItems.map((n) => ({
              key: n.key,
              label: intl.formatMessage({ id: n.labelKey }),
              children: n.children?.map((c) => ({
                key: c.key,
                label: (
                  <Flex align="center" gap={8}>
                    {renderBadge(c.key)}
                    <span>{intl.formatMessage({ id: c.labelKey })}</span>
                  </Flex>
                ),
              })),
            }))}
            style={{ borderBottom: 'none' }}
          />
          <MyButton
            variant="black.900"
            rounded={4}
            onClick={() => history.push('/contact')}
            style={{ height: 32 }}
          >
            {intl.formatMessage({ id: 'buttons.contact' })}
          </MyButton>
        </Flex>
      </Flex>
    </Header>
  );
};

export default SiteHeader;
